import { ApiService } from '../../services/api_service';
import { b64ToBlob } from '../../core/elements/helper/b64ToBlob';
import { ContextService } from '../../services/context_service';
import { DataService } from '../../services/data_service';
import { DatenauswertungCO2Fussabdruck } from './DatenauswertungCO2Fussabdruck';
import { DatenauswertungCO2FussabdruckDetails } from './DatenauswertungCO2FussabdruckDetails';
import { DatenauswertungJahrauswahlEinzeln } from '../datenauswertung/DatenauswertungJahrauswahlEinzeln';
import { Document, Page, pdf, Font } from '@react-pdf/renderer';
import { getAnhang, getAnhangExtraDokumente } from './thg/bericht/getAnhang';
import { getDeckblatt, getDeckblattBack } from './thg/bericht/getDeckblatt';
import { getFooter } from './thg/bericht/getFooter';
import { getHeader } from './thg/bericht/getHeader';
import { getInhaltsverzeichnis } from './thg/bericht/getInhaltsverzeichnis';
import { getLastYearWithValues } from './helper/getLastYearWithValues';
import { getRoutingCO2Scope3 } from '../../helpers/getRoutingCO2Scope3';
import { getStandorte } from '../../helpers/getStandorte';
import { getStyle } from './thg/bericht/getStyle';
import { getThgText } from '../../main/functions/thg/getThgText';
import { getVeranstaltungen } from '../../helpers/getVeranstaltungen';
import { getVorwort } from './thg/bericht/getVorwort';
import { hasYearValues } from './helper/hasYearValues';
import { IDs } from '../../helpers/IDs';
import { lazyInject } from '../../services/inversify';
import { Module } from './thg/bericht/Module';
import * as React from 'react';
import { RoutingIDs } from '../../helpers/RoutingIDs';
import { SectionStyle } from '../../helpers/SectionStyle';
import { WActionButton } from '../../core/elements/WActionButton';
import { WHelpIcon } from '../../core/elements/WHelpIcon';
import { WSpinner } from '../../core/elements/spinner/WSpinner';

interface DatenauswertungThgBerichtProps extends React.ClassAttributes<any> {
    data: any;
    routing: any;
    standortTid: number;
    standortName: string;
    onRoutingChange: (routing: any) => void;
    jahr?: number;
    setJSXdirect?: (content: JSX.Element) => void;
    callbackPages?: (pageNumber: number, totalPages: number) => void;
}

interface DatenauswertungThgBerichtState {
    jahr: number;
    jahrMax: number;
    jahrMin: number;
    disabled: boolean;
    showSpinner: boolean;
    showSpinnerLoading: boolean;
    pdfString?: string;
    jsxAlleJahre: any;
    jsxJahresauswertung: any;
    uploadedDocus: Array<any>;
}

export class DatenauswertungThgBericht extends React.Component<DatenauswertungThgBerichtProps, DatenauswertungThgBerichtState> {

    constructor(props: any) {
        super(props);
        const startjahr: number = ((this.props.data[IDs.ENT_KONFIGURATION] || [])[0] || {}).startjahr_datenerfassung || null;
        const jahrContext = this.props.jahr ? this.props.jahr : this.contextService.get('jahr_cockpit');
        const jahrMax: number = getLastYearWithValues(this.props.data, this.props.standortTid) || new Date().getFullYear();
        const jahrMin: number = +startjahr > 1900 ? startjahr : 2010;
        const jahr: number = jahrContext || jahrMax;
        const disabled: boolean = !hasYearValues(this.props.data, this.props.standortTid, jahr);
        this.state = {
            jahr,
            jahrMax,
            jahrMin,
            disabled,
            showSpinner: false,
            showSpinnerLoading: false,
            jsxAlleJahre: null,
            jsxJahresauswertung: null,
            uploadedDocus: null
        };
        this.onActionPdf = this.onActionPdf.bind(this);
        this.setBookmark = this.setBookmark.bind(this);
    }

    @lazyInject(ApiService) private apiService: ApiService;
    @lazyInject(ContextService) private contextService: ContextService;
    @lazyInject(DataService) private dataService: DataService;

    private bookmarks: any = {};
    private totalPages: number;
    private sectionColor: string;
    private backgroundColor: string;

    componentDidMount(): void {
        this.findExtraDokumente();
        this.setColors();
    }

    componentDidUpdate(prevProps: any, prevState: any): void {
        if (this.state.jahr !== prevState.jahr) {
            this.setState({ disabled: !hasYearValues(this.props.data, this.props.standortTid, this.state.jahr) });
            this.findExtraDokumente();
        }
    }

    async makePdf(catalogDone = false): Promise<void> {
        Font.registerHyphenationCallback((word: string) =>
            word.length > 99 ? word.match(new RegExp('.{1,100}', 'g')) : [word]
        );
        this.setState({ showSpinner: true });
        const { data, standortName, standortTid } = this.props;
        const { jahr, jsxAlleJahre, jsxJahresauswertung, uploadedDocus } = this.state;
        const pageStyle = getStyle('page');
        const logoURL = this.contextService.get('UNTERNEHMEN_LOGO_RESIZE_200_URL');
        const standorte: any[] = [...(getStandorte(this.props.data) || []), ...(getVeranstaltungen(this.props.data) || [])];
        const standortNameAll: string = standortName || (standorte[0] || {}).name || 'Standort';
        const unternehmen = getThgText('unternehmen', data, jahr, standortTid);
        const isPdf = true;
        const pdfData: JSX.Element = (
            <Document>
                <Page size='A4' orientation='portrait' style={{ padding: 0 }}>
                    {getDeckblatt(logoURL, unternehmen, jahr)}
                </Page>
                <Page size='A4' orientation='portrait' style={pageStyle}>
                    {getInhaltsverzeichnis(this.bookmarks)}
                </Page>
                <Page size='A4' orientation='portrait' style={pageStyle}>
                    {getHeader(logoURL, 'portrait', unternehmen, jahr)}
                    {getFooter(/*getThgText('vorwort_fussnote1', data, jahr, standortTid), 3*/)}
                    {getVorwort(isPdf, this.setBookmark, data, jahr, standortTid)}
                    <Module
                        isPdf
                        data={data}
                        jahr={jahr}
                        standort={standortTid}
                        getPageNum={this.setBookmark}
                    />
                </Page>
                <Page size='A4' orientation='portrait' style={pageStyle}>
                    {getHeader(logoURL, 'portrait', unternehmen, jahr, standortName)}
                    {getFooter()}
                    {getAnhang(isPdf, [null], this.setBookmark, `3. Treibhausgasbilanz${standorte.length > 1 ? 'en' : ''}`)}
                    {getAnhang(isPdf, [(jsxJahresauswertung || {})[standortTid || 0]], this.setBookmark, standortNameAll, true, 2)}
                </Page>
                <Page size='A4' orientation='landscape' style={pageStyle}>
                    {getHeader(logoURL, 'landscape', unternehmen, jahr, standortName)}
                    {getFooter()}
                    {getAnhang(isPdf, [(jsxAlleJahre || {})[standortTid || 0]], null, null)}
                </Page>
                {!standortTid && standorte.map((standort: any) => {
                    const index: string = (standort || {}).tid || this.props.standortTid || 0;
                    const name: string = (standort || {}).name;
                    return <>
                        <Page size='A4' orientation='portrait' style={pageStyle}>
                            {getHeader(logoURL, 'portrait', unternehmen, jahr, name)}
                            {getFooter()}
                            {getAnhang(isPdf, [(jsxJahresauswertung || {})[index]], this.setBookmark, name, true, 2)}
                        </Page>
                        <Page size='A4' orientation='landscape' style={pageStyle}>
                            {getHeader(logoURL, 'landscape', unternehmen, jahr, name)}
                            {getFooter()}
                            {getAnhang(isPdf, [(jsxAlleJahre || {})[index]], null, null, true)}
                        </Page>
                    </>;
                })}
                {Array.isArray(uploadedDocus) && uploadedDocus.length > 0 &&
                    <Page size='A4' orientation='portrait' style={pageStyle}>
                        {getHeader(logoURL, 'landscape', unternehmen, jahr, standortName)}
                        {getFooter()}
                        {getAnhangExtraDokumente(isPdf, uploadedDocus, this.setBookmark)}
                    </Page>
                }
                <Page size='A4' orientation='portrait' style={{ padding: 0 }}>
                    {getDeckblattBack((pageNumber: number) => this.totalPages = pageNumber)}
                </Page>
            </Document>
        );

        try {
            // catalogDone === false -> 1. Round, um das Inhaltsverzeichnis zu erstellen.
            // catalogDone === true -> 2. Round, um PDF zu erstellen.
            const blob = await pdf(pdfData).toBlob();
            const filename = `Datenauswertung_THG_Bericht_${jahr}.pdf`;
            if (catalogDone) {
                this.processPDF(blob, filename)
            } else {
                await this.makePdf(true);
            }
        } catch (error) {
            console.error(error);
            alert('PDF-Generierung ist fehlgeschlagen.');
        }

        this.setState({ showSpinner: false });
    }

    onActionPdf(): void {
        const { data, standortTid } = this.props;
        const { jahr } = this.state;
        const hasSystemgrenzen: boolean = ((((((data || {})[IDs.KENNZIFFERN_BERECHNET] || {})[jahr] || {}).thg || {})[standortTid || 'all'] || {}).flag || {}).hasSystemgrenzen;

        if (hasSystemgrenzen) {
            if (window.confirm(`Sie drucken nun den THG-Bericht für das Jahr ${this.state.jahr}`)) {
                this.makePdf();
            }
            return;
        }

        if (window.confirm('Die "Systemgrenzen der Treibhausgasbilanz" müssen zwingend angegeben werden.\nJetzt zur Eingabe der Bilanzgrenzen wechseln?')) {
            const standort: number = ((getStandorte(this.props.data) || [])[0] || {}).tid || null;
            const routing: any = {
                path: { ...RoutingIDs.ROUTE_BETRIEBLICHE_DATEN_MOTIVATION }, standort: standortTid || standort, jahr, key: 'checkCfmMotivation'
            };
            this.props.onRoutingChange({ ...routing });
        }
    }

    render(): JSX.Element {
        const { data, standortTid } = this.props;
        const { jahr } = this.state;
        const standorte: any[] = [...(getStandorte(this.props.data) || []), ...(getVeranstaltungen(this.props.data) || [])];

        return <>
            {!this.props.setJSXdirect && <>
                <div style={{ backgroundColor: this.backgroundColor, padding: '20px', display: 'flex', flex: 1, flexDirection: 'row' }}>
                    <DatenauswertungJahrauswahlEinzeln
                        jahrMax={this.state.jahrMax}
                        jahrMin={this.state.jahrMin}
                        jahr={this.state.jahr}
                        externalHeaderElements={[this.renderEingabeButton(), this.renderPdfButton()]}
                        onJahrChange={(jahr: number) => this.setState({ jahr: +jahr, jsxAlleJahre: null, jsxJahresauswertung: null },
                            () => console.log('THG Jahr Set: ', this.state.jahr))}
                    />
                    {/*<div style={{ padding: 5, paddingRight: 20 }}>
                        {this.renderHelpIcon()}
                    </div>*/}
                </div>
                <div
                    style={{
                        maxWidth: 720, padding: '3px 0 3px 16px', display: 'flex', flex: 1, flexDirection: 'column', boxSizing: 'border-box'
                    }}
                >
                    {this.state.disabled
                        ? <div style={{ marginTop: '12pt' }}>
                            Für das Jahr {this.state.jahr} sind keine Daten vorhanden.
                        </div>
                        : <>
                            <div style={{ fontSize: '16pt', fontWeight: 'bold', textAlign: 'center', color: this.sectionColor, marginTop: '12pt' }}>
                                Vorschau
                            </div>
                            {getVorwort(false, null, data, jahr, standortTid)}
                            <Module
                                data={data}
                                jahr={jahr}
                                standort={standortTid}
                            />
                        </>
                    }
                </div>
                {!this.state.disabled &&
                    <div style={{ width: 0, height: 0, overflow: 'hidden'/*, padding: '0 0 3px 16px'*/ }}>
                        {this.renderJahresauswertung()}
                        {this.renderAlleJahre()}
                        {!standortTid && standorte.map((standort: any, index: number) =>
                            <React.Fragment key={`standort-${index}`}>
                                {this.renderJahresauswertung(standort)}
                                {this.renderAlleJahre(standort)}
                            </React.Fragment>
                        )}
                    </div>
                }
            </>}
            {this.state.showSpinner && <WSpinner label='PDF...' />}
            {this.state.showSpinnerLoading && <WSpinner />}
        </>;
    }

    renderAlleJahre(standort?: any): JSX.Element {
        const standortName: string = (standort || {}).tid ? this.props.standortName : (standort || {}).name;
        const standortTid: number = (standort || {}).tid || this.props.standortTid;
        const index: number = standortTid || 0;

        return (
            <DatenauswertungCO2Fussabdruck
                key={`alle_jahre-${this.state.jahr}-${index}`}
                id={`alle_jahre-${this.state.jahr}-${index}`}
                data={this.props.data}
                onRoutingChange={() => { }}
                routing={this.props.routing}
                standortTid={standortTid}
                standortName={standortName}
                jahrFirst={this.state.jahrMin}
                jahrLast={this.state.jahrMax}
                pdfLayoutless
                setJSXdirect={(element: JSX.Element) => {
                    if (!(this.state.jsxAlleJahre || {})[index]) {
                        this.setState({ jsxAlleJahre: { ...this.state.jsxAlleJahre, [index]: element } });
                    }
                }}
            />
        );
    }

    renderJahresauswertung(standort?: any): JSX.Element {
        const { jahr } = this.state;
        const standortName: string = (standort || {}).tid ? (standort || {}).name : this.props.standortName;
        const standortTid: number = (standort || {}).tid || this.props.standortTid;
        const index: number = standortTid || 0;
        console.log('THG Jahresauswertung: ', jahr, standort);
        return (
            <DatenauswertungCO2FussabdruckDetails
                key={`jahresauswertung-${jahr}-${index}`}
                id={`jahresauswertung-${jahr}-${index}`}
                data={this.props.data}
                onRoutingChange={() => { }}
                routing={this.props.routing}
                standortTid={standortTid}
                standortName={standortName}
                jahr={jahr}
                autoOpen
                pdfLayoutless
                setJSXdirect={(element: JSX.Element) => {
                    console.log('THG Jahresauswertung Set: ', jahr, index);
                    if (!(this.state.jsxJahresauswertung || {})[index]) {
                        this.setState({ jsxJahresauswertung: { ...this.state.jsxJahresauswertung, [index]: element } });
                    } else {
                        console.log('THG Jahresauswertung Set Exists: ', jahr, index);
                        const jsxJA = this.state.jsxJahresauswertung;
                        jsxJA[index] = element;
                        this.setState({ jsxJahresauswertung: jsxJA });
                    }
                }}
            />
        );
    }

    renderEingabeButton(): JSX.Element {
        const { jahr } = this.state;

        return (
            <WActionButton
                styleProps={{ color: 'white', backgroundColor: this.sectionColor, padding: '1px 8px 1px 2px' }}
                label='Dateneingabe'
                insideText='Dateneingabe'
                tooltip='Zum Dateneingabebereich springen'
                key='dateneingabe'
                action='edit'
                onAction={() => this.props.onRoutingChange({
                    ...(getRoutingCO2Scope3(this.props.data, this.props.standortTid)), jahr, key: 'inputScope3'
                })}
            />
        );
    }

    renderHelpIcon(): JSX.Element {
        return (
            <WHelpIcon
                data={this.props.data}
                styleDivProps={{ marginLeft: 5, display: 'flex', alignSelf: 'center' }}
                styleProps={{ margin: 0 }}
                label='THG-Bericht - Hinweise'
                helpKey='datenauswertung_thg_bericht'
                type='theme_info'
                style='theme_small'
                customElement='Info'
                editBar
                useTheme
            />
        );
    }

    renderPdfButton(): JSX.Element {
        const disabled: boolean = !hasYearValues(this.props.data, this.props.standortTid, this.state.jahr) || !this.state.uploadedDocus;
        const styleProps: React.CSSProperties = disabled
            ? { backgroundColor: 'lightgray', cursor: 'not-allowed' }
            : { color: 'white', backgroundColor: this.sectionColor, padding: '1px 8px 1px 2px' };
        return (
            <WActionButton
                styleProps={styleProps}
                label='Bericht erstellen'
                insideText='Bericht erstellen'
                key='pdf'
                action='pdf'
                disabled={disabled}
                onAction={this.onActionPdf}
            />
        );
    }

    setBookmark(key: string, page: number, level: number): void {
        this.bookmarks[key] = { page, level };
    }

    findExtraDokumente() {
        const { jahr } = this.state;
        const { data, standortTid } = this.props;
        // 1. docuRowTids finden
        const docuRowTids: Array<number> = data[IDs.ENT_CO2_SCOPE3_AUSWAHL].filter((r: any) =>
            r.jahr === jahr
            && r.name === 'dokument'
            && hasYearValues(data, r.standort_tid_orig || r.standort_tid, jahr)
            && (standortTid ? (r.standort_tid_orig || r.standort_tid) === standortTid : true))
            .map((r: any) => r.tid);
        if (docuRowTids.length > 0) {
            // 2. Dokus in Gallery finden
            const galKeys: Array<string> = docuRowTids.map((tid: number) => `${IDs.ENT_CO2_SCOPE3_AUSWAHL}_${tid}`);
            this.setState({ showSpinnerLoading: true, uploadedDocus: null }, () => {
                this.dataService.loadData(IDs.ENT_GALLERY,
                    // [{ field: 'parent_entity', op: 'IN', val: "('" + galKeys.join("', '") + "')" }],
                    { parent_entity: galKeys },
                    { fieldList: 'name, foto' },
                    (res: any) => {
                        // console.log(res, JSON.stringify(res).length);
                        const uploadedDocus = (res[IDs.ENT_GALLERY] || []).map((r: any) => {
                            return {
                                tid: r.foto,
                                name: r.name || 'Keine Angabe'
                            };
                        });
                        console.log(docuRowTids, galKeys, uploadedDocus);
                        this.setState({ uploadedDocus, showSpinnerLoading: false });
                    }, IDs.MOD_ETOOL);
            });
        } else {
            this.setState({ uploadedDocus: [] });
        }
    }

    processPDF(blob: Blob, filename: string) {
        const { jahr, uploadedDocus } = this.state;
        const { standortTid } = this.props;
        const { totalPages } = this;
        const callback = (blob: Blob, filename: string) => {
            console.log(blob, filename);
            saveAs(blob, filename)
        };
        if (uploadedDocus.length > 0) {
            // Blob to Base64
            const that = this;
            const reader = new FileReader();
            reader.readAsDataURL(blob);
            reader.onload = function (event: any) {
                const pdfB64 = event.target.result;
                console.log('PDF-Dateigröße ohne Anhänge:', (pdfB64.length / 1024 / 1024).toFixed(2), 'MB');
                // Dokumente an Berichte anhängen
                const docuIds = uploadedDocus.map((r: any) => r.tid);
                that.apiService.call('attachDocuToTHGBericht',
                    { docuIds, pdfB64, jahr, standort: standortTid || 'gesamt', totalPages },
                    (json: string, succ: boolean) => {
                        if (succ && json) {
                            let obj: any;
                            try {
                                obj = JSON.parse(json);
                            } catch (err) {
                                console.log(succ, json);
                            }
                            if (obj && obj.pdfB64) {
                                b64ToBlob(obj.pdfB64, 'application/pdf', (blob: any) => {
                                    console.log('PDF-Dateigröße mit Anhänge:', (blob.size / 1024 / 1024).toFixed(2), 'MB');
                                    callback(blob, filename);
                                });
                                if (Array.isArray(obj.error) && obj.error.length > 0) {
                                    // console.log(obj.error);
                                    let msg: string = "Folgende Dateien wurden dem Dokument nicht beigefügt.\n\n";
                                    obj.error.forEach((e: any) => {
                                        const doc = uploadedDocus.find((d: any) => d.tid === +e.tid);
                                        if (doc) {
                                            msg += `${doc.name}\n[${e.file}]\n${e.info}\n\n`;
                                        }
                                    });
                                    alert(msg);
                                }
                            } else {
                                callback(blob, filename);
                            }
                        } else {
                            callback(blob, filename);
                        }
                    }, IDs.MOD_ETOOL);
            };
        } else {
            callback(blob, filename);
        }
    }

    setColors(): void {
        this.sectionColor = (SectionStyle.getSectionColors() || {}).datenauswertung;
        this.backgroundColor = new SectionStyle().getPropertyValue('--color-wform-background');
    }
}
